import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  Paper,
  styled,
} from "@mui/material";
import React from "react";

import {
  CONTENT_CONFIDENCE_THRESHOLD_ANS_BY_ML,
  CONTENT_CONFIDENCE_THRESHOLD_SYS_ACC
} from "components/detector/LineChartMetrics/ContentStrings";
import { Typography } from "new-components/typography/Typography";
import { EvaluationResults } from "types/detectors";

import { ConfidenceSliderAccuracyChart } from "./ConfidenceSliderAccuracyChart";


export const NAIVE_SYSTEM_ACCS = {
	"__0.5__mean": 0.7518549999999999,
	"__0.51__mean": 0.76166,
	"__0.52__mean": 0.771575,
	"__0.53__mean": 0.78101,
	"__0.54__mean": 0.7902899999999999,
	"__0.55__mean": 0.79925,
	"__0.56__mean": 0.807955,
	"__0.57__mean": 0.81684,
	"__0.58__mean": 0.8251850000000001,
	"__0.59__mean": 0.833575,
	"__0.6__mean": 0.84155,
	"__0.61__mean": 0.849495,
	"__0.62__mean": 0.85712,
	"__0.63__mean": 0.864575,
	"__0.64__mean": 0.87204,
	"__0.65__mean": 0.87915,
	"__0.66__mean": 0.8858550000000001,
	"__0.67__mean": 0.892485,
	"__0.68__mean": 0.898955,
	"__0.69__mean": 0.9054,
	"__0.7__mean": 0.9115500000000001,
	"__0.71__mean": 0.917365,
	"__0.72__mean": 0.92292,
	"__0.73__mean": 0.92832,
	"__0.74__mean": 0.9335800000000001,
	"__0.75__mean": 0.9385600000000001,
	"__0.76__mean": 0.943685,
	"__0.77__mean": 0.9482299999999999,
	"__0.78__mean": 0.9527,
	"__0.79__mean": 0.957005,
	"__0.8__mean": 0.960845,
	"__0.81__mean": 0.964805,
	"__0.82__mean": 0.96862,
	"__0.83__mean": 0.972105,
	"__0.84__mean": 0.97548,
	"__0.85__mean": 0.9786250000000001,
	"__0.86__mean": 0.981385,
	"__0.87__mean": 0.9839800000000001,
	"__0.88__mean": 0.98665,
	"__0.89__mean": 0.9889,
	"__0.9__mean": 0.99095,
	"__0.91__mean": 0.9926699999999999,
	"__0.92__mean": 0.994335,
	"__0.93__mean": 0.99568,
	"__0.94__mean": 0.9967900000000001,
	"__0.95__mean": 0.99778,
	"__0.96__mean": 0.99858,
	"__0.97__mean": 0.99924,
	"__0.98__mean": 0.99973,
	"__0.99__mean": 0.99995,
	"__1.0__mean": 1.0,
};

export const NAIVE_ML_ACCS = {
	"__0.5__mean": 1.0,
	"__0.51__mean": 0.98,
	"__0.52__mean": 0.96,
	"__0.53__mean": 0.94,
	"__0.54__mean": 0.92,
	"__0.55__mean": 0.9,
	"__0.56__mean": 0.88,
	"__0.57__mean": 0.86,
	"__0.58__mean": 0.84,
	"__0.59__mean": 0.8200000000000001,
	"__0.6__mean": 0.8,
	"__0.61__mean": 0.78,
	"__0.62__mean": 0.76,
	"__0.63__mean": 0.74,
	"__0.64__mean": 0.72,
	"__0.65__mean": 0.7,
	"__0.66__mean": 0.6799999999999999,
	"__0.67__mean": 0.6599999999999999,
	"__0.68__mean": 0.64,
	"__0.69__mean": 0.62,
	"__0.7__mean": 0.6,
	"__0.71__mean": 0.5800000000000001,
	"__0.72__mean": 0.56,
	"__0.73__mean": 0.54,
	"__0.74__mean": 0.52,
	"__0.75__mean": 0.5,
	"__0.76__mean": 0.48,
	"__0.77__mean": 0.45999999999999996,
	"__0.78__mean": 0.43999999999999995,
	"__0.79__mean": 0.42000000000000004,
	"__0.8__mean": 0.4,
	"__0.81__mean": 0.38,
	"__0.82__mean": 0.36,
	"__0.83__mean": 0.33999999999999997,
	"__0.84__mean": 0.31999999999999995,
	"__0.85__mean": 0.29999999999999993,
	"__0.86__mean": 0.28,
	"__0.87__mean": 0.26,
	"__0.88__mean": 0.24,
	"__0.89__mean": 0.21999999999999997,
	"__0.9__mean": 0.19999999999999996,
	"__0.91__mean": 0.17999999999999994,
	"__0.92__mean": 0.16000000000000003,
	"__0.93__mean": 0.14,
	"__0.94__mean": 0.12,
	"__0.95__mean": 0.09999999999999998,
	"__0.96__mean": 0.07999999999999996,
	"__0.97__mean": 0.05999999999999994,
	"__0.98__mean": 0.040000000000000036,
	"__0.99__mean": 0.020000000000000018,
	"__1.0__mean": 0.0,
};

const CustomAccordion = styled((props: AccordionProps) => <Accordion disableGutters elevation={0} square {...props} />)(
	() => ({
		backgroundColor: "transparent",
		display: "flex",
		flexDirection: "column-reverse",
		"&:not(:last-child)": { borderBottom: 0 },
		"&::before": { display: "none" },
		"& > .MuiAccordionSummary-root": { minHeight: 0 },
	})
);

interface ConfidenceSliderChartsProps {
	value: number;
	minConfidence: number;
	maxConfidence: number;
	isAccordionExpanded: boolean;
	handleChartVisibility: (value: boolean) => void;
	evaluationResults?: EvaluationResults | null;
}

export function ConfidenceSliderCharts(props: ConfidenceSliderChartsProps) {
	const {
		value,
		minConfidence,
		maxConfidence,
		isAccordionExpanded,
		evaluationResults,
		handleChartVisibility,
	} = props;

	return (
		<Box id="confidence-slider-charts">
			<CustomAccordion
				expanded={isAccordionExpanded}
				onChange={() => {
					handleChartVisibility(!isAccordionExpanded);
				}}
			>
				<AccordionSummary
					expandIcon={<ExpandMore />}
					aria-controls="confidence-threshold-content"
					id="confidence-threshold-header"
				/>
				<AccordionDetails sx={{ px: 0, pb: 0 }}>
					<Paper elevation={0} sx={{ textAlign: "center" }}>
						<ConfidenceSliderAccuracyChart
							data={
								evaluationResults?.balanced_system_accuracies &&
									Object.values(evaluationResults?.balanced_system_accuracies).length > 0
									? evaluationResults.balanced_system_accuracies
									: NAIVE_SYSTEM_ACCS
							}
							thresholdValue={value}
							syncId="confidence-threshold"
							color="#529FE8"
							tooltipTitle={
								<>
									Projected <br /> System Accuracy
								</>
							}
							chartInfoTooltip={{
								title: "Projected System Accuracy",
								content: CONTENT_CONFIDENCE_THRESHOLD_SYS_ACC,
							}}
							chartTitle="Projected System Accuracy"
							showDashedLine={
								!(
									evaluationResults?.balanced_system_accuracies &&
									Object.values(evaluationResults?.balanced_system_accuracies).length > 0
								)
							}
							xMin={minConfidence}
							xMax={maxConfidence}
							yTicks={[0.5, 0.75, 1]}
						/>
					</Paper>
					<Paper elevation={0} sx={{ textAlign: "center" }}>
						<ConfidenceSliderAccuracyChart
							data={
								evaluationResults?.ml_answer_proportions &&
									Object.values(evaluationResults?.ml_answer_proportions).length > 0
									? evaluationResults.ml_answer_proportions
									: NAIVE_ML_ACCS
							}
							thresholdValue={value}
							syncId="confidence-threshold"
							color="#00C7D9"
							tooltipTitle={
								<>
									Answered <br /> by ML
								</>
							}
							chartInfoTooltip={{
								title: "Percent answered by ML",
								content: CONTENT_CONFIDENCE_THRESHOLD_ANS_BY_ML,
							}}
							chartTitle="Percent answered by ML"
							showDashedLine={
								!(
									evaluationResults?.ml_answer_proportions &&
									Object.values(evaluationResults?.ml_answer_proportions).length > 0
								)
							}
							xMin={minConfidence}
							xMax={maxConfidence}
							yTicks={[0, 0.25, 0.5, 0.75, 1]}
						/>
					</Paper>
					<Box textAlign="start" px={2} pb={1}>
            <Typography variant="small" color="text.disabled">
							High confidence threshold setting increases system accuracy and reduces the % of queries that will be
							answered solely by ML. When threshold set to 1.0, ALL queries are answered by cloud labelers.
						</Typography>
					</Box>
				</AccordionDetails>
			</CustomAccordion>
		</Box>
	);
}
